import type { ToolAvailability } from "@/hooks/types";

/**
 * How Discover's lists can be ordered (Jira NAV-28).
 *
 * A runtime list as well as a type, because the value arrives in the address
 * and has to be checked against something before the page trusts it. Derived
 * from a `satisfies`-checked record for the reason `TOOL_AVAILABILITIES` is: an
 * array annotated with the union type-checks while missing a member.
 *
 * `name` is first because it is the default, and a default is what an address
 * without `?sort=` gets.
 */
const DISCOVER_SORT_KEYS = {
  name: true,
  users: true,
  availability: true,
} satisfies Record<string, true>;

export type DiscoverSort = keyof typeof DISCOVER_SORT_KEYS;

export const DISCOVER_SORTS = Object.keys(
  DISCOVER_SORT_KEYS,
) as readonly DiscoverSort[];

export const DEFAULT_DISCOVER_SORT: DiscoverSort = "name";

/**
 * The availability vocabulary from least to most in the reader's way: what
 * they already have, then what they take in one click, then the two kinds of
 * request, then what they cannot ask for yet, then what they cannot ask for at
 * all.
 *
 * Not `TOOL_AVAILABILITIES`, which is the order an admin *authors* them in and
 * says nothing about which a member reaches first. A record rather than an
 * array so a new availability is a compile error here instead of a tool that
 * sorts to the end with no rank.
 */
const EASE_RANK: Record<ToolAvailability, number> = {
  PREINSTALLED: 0,
  READY: 1,
  PASSIVE: 2,
  QUICK: 3,
  STANDARD: 4,
  UNDER_REVIEW: 5,
  BLOCKED: 6,
};

interface Sortable {
  tool: { id: string; name: string };
  availability: ToolAvailability;
}

/**
 * The same list in the requested order, never the input array itself.
 *
 * Every order falls back to the name, so two tools with the same count or the
 * same availability never swap places between renders — the one thing a list
 * somebody is scanning must not do.
 *
 * `users` reads `activeUsers`, the unit's own counts by tool id. A tool with no
 * count is a tool nobody here has taken up, which sorts as zero: the counts
 * are only written for tools somebody holds. The caller decides what to do
 * when the counts could not be read at all, because "we could not tell" is not
 * "nobody uses anything".
 */
export function sortForDiscover<T extends Sortable>(
  items: readonly T[],
  sort: DiscoverSort,
  activeUsers: ReadonlyMap<string, number>,
  locale?: string,
): T[] {
  const byName = (a: T, b: T) =>
    a.tool.name.localeCompare(b.tool.name, locale, { sensitivity: "base" });
  const compare: (a: T, b: T) => number =
    sort === "users"
      ? (a, b) =>
          (activeUsers.get(b.tool.id) ?? 0) -
            (activeUsers.get(a.tool.id) ?? 0) || byName(a, b)
      : sort === "availability"
        ? (a, b) =>
            EASE_RANK[a.availability] - EASE_RANK[b.availability] ||
            byName(a, b)
        : byName;
  return [...items].sort(compare);
}

/** The items a Discover list keeps under an availability filter; all of them
 *  when there is none. */
export function filterByAvailability<T extends Sortable>(
  items: readonly T[],
  availability: ToolAvailability | undefined,
): readonly T[] {
  return availability === undefined
    ? items
    : items.filter((item) => item.availability === availability);
}
